import { useEffect, useRef, useState } from "react";
import { Clipboard, CreditCard, Pencil, Trash2, ShoppingCart, RefreshCw, X, WalletIcon, QrCodeIcon, KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth, useMessage, useTheme } from "../../../global-components/src";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { Frame } from "../../../types";
import { formatCurrency } from "../../../utils/numbers";
import { errorMessage } from "../../../global-components/src/utils/text";
import { Icon } from "../../../global-components/src/components/icon";
import { useNavigate } from "react-router-dom";
import { PaymentTypes } from "../../../global-components/src/types";
import { getImagePathBucketPacoca } from "../../../global-components/src/utils/utils";
import "./frames-options.css";

interface FramesOptionsContentProps {
    frame?: Frame | null;
    onClose?: () => void;
    onDelete?: () => void;
    open?: boolean;
}

const FramesOptionsContent: React.FC<FramesOptionsContentProps> = ({ frame, onClose, onDelete, open }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const { setMessage } = useMessage();
    const { token, user, isMaster, isSuper } = useAuth();
    const { isDarkMode } = useTheme();
    const [errorsInput, setErrorsInput] = useState<Record<string, string[]>>({});
    const [key, setKey] = useState("");
    const [paymentMethod, setPaymentMethod] = useState<PaymentTypes>("wallet");
    const navigate = useNavigate();
    const [loadingWallet, setLoadingWallet] = useState(true);
    const [copiado, setCopiado] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [formatedAmount, setFormatedAmount] = useState(0);
    const [oldValue, setOldValue] = useState(sessionStorage.getItem(`oldValueFrame${frame?.id}`) || "");

    const [qrCode, setQrCode] = useState<string | null>(() => {
        return sessionStorage.getItem(`qrCodeFrame${frame?.id}`) ?? null;
    });
    const [copyPaste, setCopyPaste] = useState<string | null>(() => {
        return sessionStorage.getItem(`copyPasteFrame${frame?.id}`) ?? null;
    });
    const [transaction, setTransaction] = useState<any>(() => {
        const saved = sessionStorage.getItem(`transactionFrame${frame?.id}`);
        return saved ? JSON.parse(saved) : null;
    });

    const canAdmin = user?.permissions?.includes("create_frame") || isSuper();

    useEffect(() => {
        if (qrCode) sessionStorage.setItem(`qrCodeFrame${frame?.id}`, qrCode);
        if (copyPaste) sessionStorage.setItem(`copyPasteFrame${frame?.id}`, copyPaste);
        if (transaction) sessionStorage.setItem(`transactionFrame${frame?.id}`, JSON.stringify(transaction));
        if (formatedAmount && !qrCode) sessionStorage.setItem(`oldValueFrame${frame?.id}`, String(formatedAmount));
    }, [qrCode, copyPaste, transaction, formatedAmount]);

    useEffect(() => {
        if (frame?.price) setFormatedAmount(frame.price);
        setOldValue(formatCurrency(String(frame?.price)));
    }, [frame]);

    useEffect(() => {
        if (formatedAmount > walletBalance) setPaymentMethod("gerencianet_pix");
    }, [formatedAmount]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (transaction?.gateway_txid) verifyPayment();
        }, 2000);
        return () => clearInterval(interval);
    }, [transaction]);

    useEffect(() => {
        setError("");
        setKey("");
        setErrorsInput({});
    }, [open]);

    useEffect(() => {
        setLoadingWallet(true);
        if (!frame?.is_paid || !open) return;
        axios.get(`${PACOCA_API_URL}/wallet`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setWalletBalance(Number(res.data.balance));
        }).finally(() => setLoadingWallet(false));
    }, [frame, open]);

    const verifyPayment = async (manual = false) => {
        if (manual) setIsLoading(true);
        try {
            const res = await axios.post(`${PACOCA_API_URL}/frames/verify-payment`,
                { gateway_txid: transaction.gateway_txid },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.status === "completed") {
                setMessage(t("frames.paymentConfirmed"), "success");
                cancel();
                navigate(`/@${user?.user_name}`);
            }
            if (manual) setMessage(t("frames.paymentNotConfirmed"), "error");
        } catch (err: any) {
            const msg = errorMessage(err);
            if (err?.response?.data.errors) setErrorsInput(err.response.data.errors);
            else setMessage(msg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const copyLink = async () => {
        if (!frame?.key) return;
        await navigator.clipboard.writeText(frame.key);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    const copyQrCode = async () => {
        if (!copyPaste) return;
        await navigator.clipboard.writeText(copyPaste);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    const cancel = () => {
        setOldValue("");
        setQrCode(null);
        setCopyPaste(null);
        setTransaction(null);
        sessionStorage.removeItem(`qrCodeFrame${frame?.id}`);
        sessionStorage.removeItem(`copyPasteFrame${frame?.id}`);
        sessionStorage.removeItem(`transactionFrame${frame?.id}`);
        sessionStorage.removeItem(`oldValueFrame${frame?.id}`);
    };

    const buy = async () => {
        try {
            const res = await axios.post(`${PACOCA_API_URL}/frames/buy`,
                { frame_id: frame?.id, gateway: paymentMethod },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (paymentMethod === "wallet") {
                setMessage(t("frames.frameAdded"), "success");
                cancel();
                navigate(`/@${user?.user_name}`);
                return;
            }
            setTransaction(res.data.transaction);
            if (res.data.gateway_data?.qrcode) {
                setQrCode(res.data.gateway_data.qrcode);
                setCopyPaste(res.data.gateway_data.copyPaste);
            }
        } catch (err: any) {
            const msg = errorMessage(err);
            if (err?.response?.data.errors) setErrorsInput(err.response.data.errors);
            else setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFrame = async () => {
        setError("");
        setErrorsInput({});
        setIsLoading(true);

        if (!frame?.owned && frame?.is_paid && paymentMethod !== "code") {
            buy();
            return;
        }

        try {
            const errors: Record<string, string[]> = {};
            if (!frame?.owned && frame?.is_paid && paymentMethod === "code" && !canAdmin && !key.trim()) {
                errors.key = [t("frames.activationKeyRequired")];
            }
            if (Object.keys(errors).length > 0) throw { response: { data: { errors } } };

            const theKey = canAdmin ? frame?.key : key;
            await axios.post(`${PACOCA_API_URL}/frames/redeem`,
                { key: theKey, frame_id: frame?.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setKey("");
            setMessage(frame?.owned ? t("frames.frameActivated") : t("frames.frameAdded"), "success");
            navigate(`/@${user?.user_name}`);
            if (onClose) onClose();
        } catch (err: any) {
            const msg = errorMessage(err);
            if (err?.response?.data.errors) setErrorsInput(err.response.data.errors);
            else setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = async () => {
        try {
            setIsLoading(true);
            await axios.post(`${PACOCA_API_URL}/frames/remove`, {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage(t("frames.frameRemoved"), "success");
            navigate(`/@${user?.user_name}`);
        } catch (err: any) {
            const msg = errorMessage(err);
            if (err?.response?.data.errors) setErrorsInput(err.response.data.errors);
            else setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const frameImg = frame?.image_path ? getImagePathBucketPacoca(frame.image_path) : null;

    // ── STATUS LABEL ──────────────────────────────────────────
    const statusLabel = (() => {
        if (frame?.is_active_for_user) return t("frames.activeOnProfile");
        if (frame?.owned) return t("frames.yourFrame");
        if (!frame?.is_paid) return t("frames.free");
        return `R$ ${Number(frame?.price ?? 0).toFixed(2).replace(".", ",")}`;
    })();

    // ── VIEW: QR CODE ─────────────────────────────────────────
    if (qrCode) {
        return (
            <div className="foc-body">
                <div className="foc-frame-header">
                    <div className="foc-img-wrap">
                        {frameImg ? <img src={frameImg} alt={frame?.name} /> : <div className="foc-img-placeholder" />}
                    </div>
                    <div className="foc-info">
                        <div className="foc-name">{frame?.name}</div>
                        <div className="foc-status">{t("frames.waitingPayment")}</div>
                    </div>
                </div>
                <div className="foc-qr-view">

                    {/* <div className="foc-price-row">
                        <span className="foc-price-label">Valor</span>
                        <span className="foc-price-value">R$ {oldValue}</span>
                    </div> */}

                    <div className="foc-qr-hint">{t("frames.qrCodeHint")}</div>
                    <div className="foc-qr-hint">R$ {oldValue}</div>
                    <img src={qrCode} alt="QR Code PIX" className="foc-qr-img" />

                    <div className="foc-actions">
                        <button type="button" className="foc-btn foc-btn-primary" onClick={copyQrCode}>
                            <Clipboard size={15} />
                            {copiado ? t("frames.copied") : t("frames.copyPixCode")}
                        </button>
                        <button type="button" className="foc-btn foc-btn-primary" onClick={() => verifyPayment(true)} disabled={isLoading}>
                            {isLoading ? <Spinner /> : <><RefreshCw size={15} /> {t("frames.checkPayment")}</>}
                        </button>
                    </div>
                    <button type="button" className="foc-cancel-link" onClick={cancel}>{t("frames.cancel")}</button>
                </div>
            </div>
        );
    }

    // ── VIEW: MOLDURA ATIVA ───────────────────────────────────
    if (frame?.is_active_for_user) {
        return (
            <div className="foc-body">
                <div className="foc-frame-header">
                    <div className="foc-img-wrap">
                        {frameImg ? <img src={frameImg} alt={frame?.name} /> : <div className="foc-img-placeholder" />}
                        <div className="foc-active-dot" />
                    </div>
                    <div className="foc-info">
                        <div className="foc-name">{frame?.name}</div>
                        <div className="foc-status foc-status-active">{t("frames.activeOnProfile")}</div>
                    </div>
                </div>

                {canAdmin && frame?.key && (
                    <div className="foc-code-box">
                        <div>
                            <div className="foc-code-label">{t("frames.redeemCode")}</div>
                            <code className="foc-code-value">{frame.key}</code>
                        </div>
                        <button type="button" className="foc-btn foc-btn-outline foc-btn-sm" onClick={copyLink}>
                            <Clipboard size={13} />
                            {copiado ? t("frames.copied") : t("frames.copy")}
                        </button>
                    </div>
                )}

                {error && <div className="foc-error-box">{error}</div>}

                <div className="foc-actions foc-actions-col">
                    <button type="button" className="foc-btn foc-btn-ghost" onClick={handleRemove} disabled={isLoading}>
                        {isLoading ? <Spinner /> : <><Trash2 size={15} /> {t("frames.removeFrame")}</>}
                    </button>
                </div>
            </div>
        );
    }

    // ── VIEW: MOLDURA PRÓPRIA (não ativa) ─────────────────────
    if (frame?.owned) {
        return (
            <div className="foc-body">
                <div className="foc-frame-header">
                    <div className="foc-img-wrap">
                        {frameImg ? <img src={frameImg} alt={frame?.name} /> : <div className="foc-img-placeholder" />}
                    </div>
                    <div className="foc-info">
                        <div className="foc-name">{frame?.name}</div>
                        <div className="foc-status">{t("frames.yourFrame")}</div>
                    </div>
                </div>

                {error && <div className="foc-error-box">{error}</div>}

                <div className="foc-actions foc-actions-col">
                    <button type="button" className="foc-btn foc-btn-primary" onClick={handleFrame} disabled={isLoading}>
                        {isLoading ? <Spinner /> : t("frames.activateFrame")}
                    </button>
                </div>
            </div>
        );
    }

    // ── VIEW: AQUISIÇÃO (grátis ou paga) ──────────────────────
    const isBuyingWithCode = paymentMethod === "code";
    const actionLabel = (() => {
        if (!frame?.is_paid) return t("frames.addForFree");
        if (isBuyingWithCode) return t("frames.redeem");
        return t("frames.buy");
    })();

    return (
        <div className="foc-body">
            <div className="foc-frame-header">
                <div className="foc-img-wrap">
                    {frameImg ? <img src={frameImg} alt={frame?.name} /> : <div className="foc-img-placeholder" />}
                </div>
                <div className="foc-info">
                    <div className="foc-name">{frame?.name}</div>
                    <div className="foc-status">{statusLabel}</div>
                </div>
                {canAdmin && (
                    <button type="button" className="foc-edit-btn" onClick={() => navigate(`/molduras/${frame?.id}`)}>
                        <Pencil size={15} />
                    </button>
                )}
            </div>

            {frame?.is_paid && (
                <div className="foc-price-row">
                    <span className="foc-price-label">{t("frames.frameValue")}</span>
                    <span className="foc-price-value">R$ {formatCurrency(String(frame.price))}</span>
                </div>
            )}

            {frame?.is_paid && (
                <>
                    <div className="foc-section-label">{t("frames.paymentMethod")}</div>
                    <div className="foc-pay-list">
                        {!canAdmin && (
                            <>
                                <label
                                    className={`foc-pay-item ${paymentMethod === "wallet" ? "sel" : ""} ${formatedAmount > walletBalance ? "bank-pay-disabled" : ""}`}
                                >
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        className="sr-only"
                                        checked={paymentMethod === "wallet"}
                                        disabled={formatedAmount > walletBalance}
                                        onChange={() => setPaymentMethod("wallet")}
                                    />
                                    <span className={`bank-radio ${paymentMethod === "wallet" ? "bank-radio-sel" : ""}`} />
                                    <span className="bank-payment-icon">
                                        <WalletIcon size={16} />
                                    </span>
                                    <div>
                                        <div className="bank-pay-title">{t("frames.walletBalance")}</div>
                                        <div className="bank-payment-sub">
                                            {loadingWallet ? (
                                                <span className="bank-placeholder-inline" />
                                            ) : (
                                                t("frames.availableBalance", { balance: walletBalance.toFixed(2).replace(".", ",") })
                                            )}
                                        </div>
                                    </div>
                                    {!loadingWallet && (
                                        <span className={`bank-pay-tag ${formatedAmount > walletBalance ? "bank-pay-tag-warn" : "bank-pay-tag-ok"}`}>
                                            {formatedAmount > walletBalance ? t("frames.insufficientBalance") : t("frames.availableTag")}
                                        </span>
                                    )}
                                </label>

                                <label className={`foc-pay-item ${paymentMethod === "gerencianet_pix" ? "sel" : ""}`}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        className="sr-only"
                                        checked={paymentMethod === "gerencianet_pix"}
                                        onChange={() => setPaymentMethod("gerencianet_pix")}
                                    />
                                    <span className={`bank-radio ${paymentMethod === "gerencianet_pix" ? "bank-radio-sel" : ""}`} />
                                    <span className="bank-payment-icon">
                                        <QrCodeIcon size={16} />
                                    </span>
                                    <div>
                                        <div className="bank-pay-title">Pix</div>
                                        <div className="bank-payment-sub">{t("frames.pixDesc")}</div>
                                    </div>
                                </label>
                            </>
                        )}

                        {(frame?.can_redeem || canAdmin) && (
                            <label className={`foc-pay-item ${paymentMethod === "code" ? "sel" : ""}`}>
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    className="sr-only"
                                    checked={paymentMethod === "code"}
                                    onChange={() => setPaymentMethod("code")}
                                />
                                <span className={`bank-radio ${paymentMethod === "code" ? "bank-radio-sel" : ""}`} />
                                <span className="bank-payment-icon">
                                    <KeyRound size={16} />
                                </span>
                                <div>
                                    <div className="bank-pay-title">{t("frames.activationKey")}</div>
                                    <div className="bank-payment-sub">{t("frames.activationKeyDesc")}</div>
                                </div>
                            </label>
                        )}
                    </div>

                    {isBuyingWithCode && frame?.can_redeem && (
                        <div className="foc-key-wrap">
                            <div className="foc-key-label">{t("frames.activationKey")}</div>
                            <input
                                type="text"
                                value={canAdmin && frame.key ? frame.key : key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="ABSA-XXXX-YYYY"
                                disabled={canAdmin}
                                autoCapitalize="none"
                                className={`foc-key-input ${errorsInput.key ? "foc-key-input-error" : ""}`}
                            />
                            {errorsInput.key && <div className="foc-error-msg">{errorsInput.key[0]}</div>}
                        </div>
                    )}
                </>
            )}

            {error && <div className="foc-error-box">{error}</div>}

            <div className="foc-actions">
                {canAdmin && frame?.can_redeem && frame?.is_paid && (
                    <button type="button" className="foc-btn foc-btn-primary" onClick={copyLink} disabled={isLoading}>
                        <Clipboard size={15} />
                        {copiado ? t("frames.copied") : t("frames.copyCode")}
                    </button>
                )}
                <button type="button" className="foc-btn foc-btn-primary" onClick={handleFrame} disabled={isLoading}>
                    {isLoading ? <Spinner /> : <><ShoppingCart size={15} /> {actionLabel}</>}
                </button>
            </div>
        </div>
    );
};

export default FramesOptionsContent;